// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    addToChainAfter,
    addToChainBefore,
    createHandlerChainRunner,
    executeAwaitableHandlerChain,
    Handler,
    HasOnError,
    HasOnSuccess,
    replaceInChain,
} from "@insite/client-framework/HandlerCreator";
import {
    getAlsoPurchasedProductsCollectionV2,
    GetProductCollectionApiV2Parameter,
    getProductCollectionV2,
    GetRelatedProductCollectionApiV2Parameter,
    getRelatedProductsCollectionV2,
} from "@insite/client-framework/Services/ProductServiceV2";

import { StorefrontAccess } from "@insite/client-framework/Services/SettingsService";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { ProductCollectionModel, ProductModel } from "@insite/client-framework/Types/ApiModels";

import { getCoveoProductsRecommendation } from "@eisinc/App/Services/CoveoProductsRecommendationService";
import { getFeaturedProducts, GetFeaturedProductsApiParameter } from "@eisinc/App/Services/FeaturedProductsService";
import { xGetProductCollectionV2 } from "@eisinc/App/Services/ProductServiceV2";
import { xCustomSearchProductActions } from "@eisinc/App/Store/Reducers/xCustomSearchProduct";
import {
    chain,
    ExecuteOnSuccessCallback,
    PopulateApiParameter,
    RequestDataFromApi,
    SortCollections,
} from "@insite/client-framework/Store/Data/Products/Handlers/LoadProducts";
import sortProductCollections from "@insite/client-framework/Store/Data/Products/Handlers/SortProductCollections";

type Parameter = (GetProductCollectionApiV2Parameter | GetRelatedProductCollectionApiV2Parameter) &
    HasOnSuccess<ProductModel[]> &
    HasOnError<string>;
type Props = {
    apiParameter: GetProductCollectionApiV2Parameter | GetRelatedProductCollectionApiV2Parameter;
    apiResult: ProductCollectionModel;
};

type HandlerType = Handler<Parameter, Props>;

/**
 *
 * @param props
 * @override clean the props in case we have includeProductsInSubCategories and there is no categoryId which will cause an API error since its
 * required to have a specific categoryId for this condition
 */
export const XngModifyParametersAfterPopulate: any = (props: any) => {
    if (
        // eslint-disable-next-line no-prototype-builtins
        !props.parameter?.hasOwnProperty("categoryId") &&
        // eslint-disable-next-line no-prototype-builtins
        props.parameter?.hasOwnProperty("includeProductsInSubCategories")
    ) {
        props.parameter.includeProductsInSubCategories = false;
    }
};

/**
 * @param props
 * @override make custom call for featured products carousel
 */
export const XngRequestDataFromApi: HandlerType = async (props: any) => {
    try {
        const state = props.getState();
        const currentPage = getCurrentPage(state);
        const settings = getSettingsCollection(state);
        if (
            settings.productSettings.storefrontAccess === StorefrontAccess.SignInRequiredToBrowse &&
            !state.context.session.isAuthenticated &&
            currentPage.fields.excludeFromSignInRequired
        ) {
            return false;
        }

        if ("relationship" in props.apiParameter) {
            if (props.apiParameter.relationship === "featuredProducts") {
                // send a request to a custom api to receive the fetured products for the passed category/ brand
                const customProps: any = props.apiParameter;
                const id =
                    customProps.brand && customProps.category
                        ? customProps.brand.id.split(":")[0]
                        : !customProps.brand
                          ? customProps.category.id.split(":")[0]
                          : customProps.brand.id.split(":")[0];
                const type =
                    customProps.brand && customProps.category ? "brand" : !customProps.brand ? "category" : "brand";
                const param: GetFeaturedProductsApiParameter = {
                    id,
                    type,
                };
                props.apiResult = await getFeaturedProducts(param);
            } else if (props.apiParameter.relationship === "coveoRecommendedProducts") {
                props.apiResult = await getCoveoProductsRecommendation(props.apiParameter);
            } else {
                props.apiResult = await getRelatedProductsCollectionV2(props.apiParameter);
            }
        } else if ("productId" in props.apiParameter && props.apiParameter?.type === "alsoPurchased") {
            props.apiResult = await getAlsoPurchasedProductsCollectionV2(props.apiParameter);
        } else {
            props.apiResult = await xGetProductCollectionV2(props.apiParameter);
        }
        /*
             xng-change: https://xngage.atlassian.net/browse/EIS-3890 
             adding analyticsObject to each product to fix click event uid 
        */
        if (props.apiResult.analyticsObject && props.apiResult.products?.length) {
            if (props.parameter.id) {
                props.dispatch({
                    type: "xWidgetsCoveo/SetWidgetIdAndData",
                    widgetId: props.parameter.id,
                    analyticsObject: props.apiResult.analyticsObject,
                } as any);
            }
        }
    } catch (error) {
        if ("status" in error && error?.status === 404) {
            const isNotProductFound = true;
            props.dispatch({
                type: "xCustomSearchProduct/isNotProductFound",
                isNotProductFound,
            }) as xCustomSearchProductActions;
            return false;
        }
        throw error;
    }
};

export const XngSortCollections: HandlerType = async props => {
    if (!props.apiResult) {
        return;
    }
    await executeAwaitableHandlerChain(sortProductCollections, { products: props.apiResult.products! }, props);
};

export const XngExecuteOnSuccessCallback: HandlerType = props => {
    if (props.apiResult) {
        props.parameter.onSuccess?.(props.apiResult.products!);
    }
};

addToChainBefore(chain, PopulateApiParameter, XngModifyParametersAfterPopulate);
replaceInChain(chain, RequestDataFromApi, XngRequestDataFromApi);
replaceInChain(chain, ExecuteOnSuccessCallback, XngExecuteOnSuccessCallback);
replaceInChain(chain, SortCollections, XngSortCollections);
